// web-commands.ts — the PER-APP half of the web shape: maps oriora_plant_diary's Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each. The two derived commands (compute_plant_signals /
// compute_week_signals) are the app's on-device cadence math (signals.rs) — on web they return a
// minimal valid shell for now; a faithful web build reimplements that signal logic in JS (the known
// per-app wrinkle, see MACOS/web docs).

import type { CommandHandler, WebStore } from "./web-runtime";
import { computePlantSignalsWeb, computeWeekSignalsWeb } from "./signals-web";
import type { Plant, CareEvent } from "./store";
import { browserSearch, browserFetch } from "./web-search-browser";

const PLANTS = "plants";
const CARE_EVENTS = "care_events";
const GROWTH_LOG = "growth_log";

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
}
/** Keep only the date portion if a full timestamp slipped in (mirrors db.rs norm_date). */
function normDate(raw: unknown): string {
  return String(raw ?? "").trim().slice(0, 10);
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Plants ──────────────────────────────────────────────────────────────
    // Native lists ORDER BY name COLLATE NOCASE ASC.
    list_plants: () =>
      db
        .all(PLANTS)
        .sort((a, b) =>
          String((a as { name?: string }).name ?? "").localeCompare(String((b as { name?: string }).name ?? ""), undefined, {
            sensitivity: "base",
          }),
        ),
    get_plant: ({ id }) => db.get(PLANTS, String(id)),
    create_plant: ({ input }) => {
      const i = input as Record<string, unknown>;
      const potted = normDate(i.potted_on);
      const species = String(i.species ?? "").trim();
      const row = {
        id: uuid(),
        name: String(i.name ?? "").trim(),
        species,
        location: String(i.location ?? "").trim(),
        potted_on: potted || null,
        photo_ref: String(i.photo_ref ?? "").trim(),
        species_confidence: species ? String(i.species_confidence ?? "").trim() : "",
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      return db.insert(PLANTS, row);
    },
    update_plant: ({ id, patch }) => {
      db.update(PLANTS, String(id), { ...(patch as Record<string, unknown>), updated_at: nowIso() });
      return null;
    },
    delete_plant: ({ id }) => {
      // Native cascades care_events; mirror that so no orphans linger in the browser store.
      const pid = String(id);
      db.remove(PLANTS, pid);
      for (const ev of db.all(CARE_EVENTS)) {
        if ((ev as { plant_id?: string }).plant_id === pid) db.remove(CARE_EVENTS, ev.id);
      }
      return null;
    },

    // ── Care events ─────────────────────────────────────────────────────────
    // Native: WHERE plant_id ORDER BY done_on DESC, created_at DESC LIMIT ?.
    list_events: ({ plantId, limit }) => {
      const pid = String(plantId);
      const rows = db
        .all(CARE_EVENTS)
        .filter((r) => (r as { plant_id?: string }).plant_id === pid)
        .sort((a, b) => {
          const ao = String((a as { done_on?: string }).done_on ?? "");
          const bo = String((b as { done_on?: string }).done_on ?? "");
          if (ao !== bo) return bo.localeCompare(ao);
          const ac = String((a as { created_at?: string }).created_at ?? "");
          const bc = String((b as { created_at?: string }).created_at ?? "");
          return bc.localeCompare(ac);
        });
      const n = typeof limit === "number" ? limit : Number(limit ?? rows.length);
      return Number.isFinite(n) ? rows.slice(0, n) : rows;
    },
    create_event: ({ input }) => {
      const i = input as Record<string, unknown>;
      const row = {
        id: uuid(),
        plant_id: String(i.plant_id ?? ""),
        kind: String(i.kind ?? "note"),
        done_on: normDate(i.done_on) || todayLocal(),
        note: String(i.note ?? "").trim(),
        photo_ref: String(i.photo_ref ?? ""),
        created_at: nowIso(),
      };
      return db.insert(CARE_EVENTS, row);
    },
    delete_event: ({ id }) => {
      db.remove(CARE_EVENTS, String(id));
      return null;
    },

    // ── Growth log ──────────────────────────────────────────────────────────
    // Native: WHERE plant_id ORDER BY measured_on ASC (oldest first — a chart reads in time order).
    list_growth: ({ plantId, limit }) => {
      const pid = String(plantId);
      const rows = db
        .all(GROWTH_LOG)
        .filter((r) => (r as { plant_id?: string }).plant_id === pid)
        .sort((a, b) => {
          const ao = String((a as { measured_on?: string }).measured_on ?? "");
          const bo = String((b as { measured_on?: string }).measured_on ?? "");
          if (ao !== bo) return ao.localeCompare(bo);
          return String((a as { created_at?: string }).created_at ?? "")
            .localeCompare(String((b as { created_at?: string }).created_at ?? ""));
        });
      const n = typeof limit === "number" ? limit : Number(limit ?? rows.length);
      return Number.isFinite(n) ? rows.slice(0, n) : rows;
    },
    create_growth: ({ input }) => {
      const i = input as Record<string, unknown>;
      const height = i.height_cm === null || i.height_cm === undefined ? null : Number(i.height_cm);
      const leaves = i.leaf_count === null || i.leaf_count === undefined ? null : Number(i.leaf_count);
      // Mirror the native guard: an entry with neither number is a ghost row.
      if (height === null && leaves === null) throw new Error("Record a height or a leaf count");
      const row = {
        id: uuid(),
        plant_id: String(i.plant_id ?? ""),
        measured_on: normDate(i.measured_on) || todayLocal(),
        height_cm: height,
        leaf_count: leaves,
        note: String(i.note ?? "").trim(),
        created_at: nowIso(),
      };
      return db.insert(GROWTH_LOG, row);
    },
    delete_growth: ({ id }) => {
      db.remove(GROWTH_LOG, String(id));
      return null;
    },

    // ── Derived signals — native computes rich cadence math in signals.rs. Web shells are
    // valid-but-empty so the UI never crashes; the AI recap is degraded until that logic is
    // ported to JS. TODO: port derived logic to JS.
    compute_plant_signals: ({ plantId, today }) => {
      const plant = db.get(PLANTS, String(plantId)) as unknown as Plant | null;
      if (!plant) throw new Error("plant_not_found");
      const events = (db.all(CARE_EVENTS) as unknown as CareEvent[]).filter((e) => e.plant_id === plant.id);
      const t = String(today ?? todayLocal()).slice(0, 10);
      return computePlantSignalsWeb(plant, events, t);
    },
    compute_week_signals: ({ today }) => {
      const plants = db.all(PLANTS) as unknown as Plant[];
      const events = db.all(CARE_EVENTS) as unknown as CareEvent[];
      const t = String(today ?? todayLocal()).slice(0, 10);
      return computeWeekSignalsWeb(plants, events, t);
    },
  };
}
